import { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import { X, Circle, RotateCcw, Trophy, Award, Clock, MoveRight } from 'lucide-react'
import { TicTacToeState } from '../../../types/game.types'
import { useApp } from '../../../contexts/AppContext'
import { generateId } from '../../../lib/storage'
import Button from '../../ui/Button'
import Card, { CardHeader, CardContent } from '../../ui/Card'
import TicTacToeBoard from './TicTacToeBoard'
import {
  createInitialState,
  makeMove,
  resetBoard,
  resetGame
} from './TicTacToeLogic'

export default function TicTacToeGame() {
  const [state, setState] = useState<TicTacToeState>(createInitialState())
  const [gameStartTime, setGameStartTime] = useState<number | null>(null)
  const [gameDuration, setGameDuration] = useState<number>(0)
  const { addGameResult } = useApp()
  
  // Game timer
  useEffect(() => {
    let interval: number | null = null
    
    if (state.gameStarted && !state.gameEnded && gameStartTime) {
      interval = window.setInterval(() => {
        setGameDuration(Math.floor((Date.now() - gameStartTime) / 1000))
      }, 1000)
    } else if (state.gameEnded && interval) {
      clearInterval(interval)
    }
    
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [state.gameStarted, state.gameEnded, gameStartTime])
  
  const handleCellClick = useCallback((row: number, col: number) => {
    if (!state.gameStarted) {
      setGameStartTime(Date.now())
    }
    
    const newState = makeMove(state, row, col)
    setState(newState)
    
    // Save game result when game ends
    if (newState.gameEnded) {
      const result = {
        id: generateId(),
        gameType: 'tic-tac-toe' as const,
        score: newState.scores[newState.winner === 'X' ? 'X' : 'O'] || 0,
        winner: newState.winner,
        duration: gameDuration,
        timestamp: Date.now(),
        moves: newState.moves,
        boardState: newState.board
      }
      addGameResult(result)
    }
  }, [state, gameDuration, addGameResult])
  
  const handleResetBoard = () => {
    setState(resetBoard(state))
    setGameStartTime(null)
    setGameDuration(0)
  }
  
  const handleResetGame = () => {
    setState(resetGame())
    setGameStartTime(null)
    setGameDuration(0)
  }
  
  const getStatusMessage = () => {
    if (state.winner) {
      return `${state.winner} wins!`
    }
    if (state.isDraw) {
      return "It's a draw!"
    }
    return `${state.currentPlayer}'s turn`
  }
  
  const getStatusColor = () => {
    if (state.winner === 'X') return 'text-primary'
    if (state.winner === 'O') return 'text-secondary'
    if (state.isDraw) return 'text-text-muted'
    return state.currentPlayer === 'X' ? 'text-primary' : 'text-secondary'
  }
  
  return (
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Game Board */}
        <div className="lg:col-span-2">
          <Card shadow="lg" padding="lg">
            <CardHeader>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Tic Tac Toe</h2>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={RotateCcw}
                    onClick={handleResetBoard}
                  >
                    Reset Board
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={RotateCcw}
                    onClick={handleResetGame}
                  >
                    New Game
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            <CardContent>
              <div className="flex flex-col items-center gap-8">
                {/* Game Status */}
                <div className="text-center">
                  <motion.h3 
                    className={`text-3xl font-bold ${getStatusColor()} mb-2`}
                    key={getStatusMessage()}
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                  >
                    {getStatusMessage()}
                  </motion.h3>
                  
                  <div className="flex items-center justify-center gap-6 text-text-muted">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{gameDuration}s</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MoveRight className="w-4 h-4" />
                      <span>{state.moves} moves</span>
                    </div>
                  </div>
                </div>
                
                {/* Game Board */}
                <TicTacToeBoard
                  state={state}
                  onCellClick={handleCellClick}
                  disabled={state.gameEnded}
                />
                
                {/* Player Indicators */}
                <div className="flex items-center justify-center gap-12">
                  <div className={`flex flex-col items-center gap-2 ${state.currentPlayer === 'X' ? 'scale-110' : 'opacity-70'}`}>
                    <div className="w-16 h-16 rounded-xl bg-primary/10 border-2 border-primary flex items-center justify-center">
                      <X className="w-10 h-10 text-primary" strokeWidth={3} />
                    </div>
                    <span className="font-semibold">Player X</span>
                    <span className="text-2xl font-bold">{state.scores.X}</span>
                  </div>
                  
                  <div className="text-text-muted">VS</div>
                  
                  <div className={`flex flex-col items-center gap-2 ${state.currentPlayer === 'O' ? 'scale-110' : 'opacity-70'}`}>
                    <div className="w-16 h-16 rounded-xl bg-secondary/10 border-2 border-secondary flex items-center justify-center">
                      <Circle className="w-10 h-10 text-secondary" strokeWidth={3} />
                    </div>
                    <span className="font-semibold">Player O</span>
                    <span className="text-2xl font-bold">{state.scores.O}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Right Column - Game Info & Controls */}
        <div className="space-y-6">
          {/* Game Instructions */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Award className="w-5 h-5" />
                How to Play
              </h3>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-text-muted">
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5" />
                  <span>Players take turns placing X and O on the 3×3 grid</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5" />
                  <span>The first player to get 3 of their marks in a row (horizontally, vertically, or diagonally) wins</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5" />
                  <span>If all 9 squares are filled without a winner, the game ends in a draw</span>
                </li>
              </ul>
            </CardContent>
          </Card>
          
          {/* Game Stats */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Trophy className="w-5 h-5" />
                Game Stats
              </h3>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 rounded-lg bg-surface">
                  <div className="text-2xl font-bold text-primary">{state.scores.X}</div>
                  <div className="text-sm text-text-muted">X Wins</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-surface">
                  <div className="text-2xl font-bold text-secondary">{state.scores.O}</div>
                  <div className="text-sm text-text-muted">O Wins</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-surface">
                  <div className="text-2xl font-bold text-text-muted">{state.moves}</div>
                  <div className="text-sm text-text-muted">Total Moves</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-surface">
                  <div className="text-2xl font-bold text-text-muted">{gameDuration}</div>
                  <div className="text-sm text-text-muted">Seconds</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Quick Actions</h3>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Button
                  variant="primary"
                  fullWidth
                  icon={RotateCcw}
                  onClick={handleResetBoard}
                  disabled={!state.gameStarted}
                >
                  Reset Current Game
                </Button>
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={handleResetGame}
                >
                  Start New Match
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}